import { useEffect, useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Globe, DollarSign, Users, Percent, Facebook, BarChart3 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const LANGS = [
  { code: "it", label: "Italiano", active: true },
  { code: "en", label: "English", active: true },
  { code: "fr", label: "Français", active: false },
  { code: "de", label: "Deutsch", active: false },
  { code: "es", label: "Español", active: false },
];

const CURRENCIES = [
  { code: "EUR", label: "Euro (€)", active: true },
  { code: "GBP", label: "Sterlina (£)", active: false },
  { code: "USD", label: "Dollaro ($)", active: false },
];

const ROLES = [
  { role: "admin", desc: "Accesso completo a tutte le sezioni" },
  { role: "editor", desc: "Solo gestione blog e contenuti homepage" },
  { role: "orders", desc: "Solo gestione ordini e clienti" },
];

export default function AdminSettings() {
  const { user } = useAuth();
  const isSuper = user?.role === "superadmin";
  const [supplierDiscount, setSupplierDiscount] = useState(15);
  const [pixelId, setPixelId] = useState("");
  const [pixelEnabled, setPixelEnabled] = useState(true);
  const [ga4Id, setGa4Id] = useState("");
  const [ga4Enabled, setGa4Enabled] = useState(true);
  const [ga4Secret, setGa4Secret] = useState("");
  const [ga4SecretConfigured, setGa4SecretConfigured] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingPixel, setSavingPixel] = useState(false);
  const [savingGa4, setSavingGa4] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      setSupplierDiscount(r.data.supplier_discount_percent || 0);
      setPixelId(r.data.meta_pixel_id || "");
      setPixelEnabled(r.data.meta_pixel_enabled !== false);
      setGa4Id(r.data.ga4_measurement_id || "");
      setGa4Enabled(r.data.ga4_enabled !== false);
      setGa4SecretConfigured(!!r.data.ga4_api_configured);
    }).catch(() => {});
  }, []);

  const saveDiscount = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings/supplier-discount", { percent: Number(supplierDiscount) });
      toast.success("Sconto fornitori aggiornato");
    } catch { toast.error("Errore"); } finally { setSaving(false); }
  };

  const savePixel = async () => {
    setSavingPixel(true);
    try {
      await api.put("/admin/settings/meta-pixel", { pixel_id: pixelId.trim(), enabled: pixelEnabled });
      toast.success("Meta Pixel aggiornato — ricarica il sito pubblico per vedere il tracking attivo");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setSavingPixel(false); }
  };

  const saveGa4 = async () => {
    setSavingGa4(true);
    try {
      const payload = { measurement_id: ga4Id.trim(), enabled: ga4Enabled };
      if (ga4Secret.trim()) payload.api_secret = ga4Secret.trim();
      await api.put("/admin/settings/ga4", payload);
      toast.success("Google Analytics 4 aggiornato — ricarica il sito pubblico");
      if (ga4Secret.trim()) { setGa4Secret(""); setGa4SecretConfigured(true); }
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setSavingGa4(false); }
  };

  return (
    <AdminLayout>
      <div className="mb-6">
        <div className="font-mono-tech text-[10px] uppercase tracking-widest text-xd-muted">Configurazione</div>
        <h1 className="font-admin text-2xl font-bold">Impostazioni</h1>
      </div>

      <div className="bg-white border border-xd-cyan/40 rounded-xl p-5 mb-6 max-w-2xl" data-testid="supplier-discount-card">
        <div className="flex items-center gap-2 font-admin font-semibold mb-3"><Percent className="w-4 h-4 text-xd-cyanHover" /> Sconto Fornitori & Partner</div>
        <p className="text-xs text-xd-muted mb-3">Percentuale applicata automaticamente a tutti gli ordini effettuati da account con ruolo <strong>supplier</strong> approvato.</p>
        <div className="flex items-center gap-3">
          <input type="number" min={0} max={90} step={1} value={supplierDiscount} onChange={(e) => setSupplierDiscount(e.target.value)} data-testid="supplier-discount-input" className="w-24 h-10 px-3 rounded-lg border border-xd-border text-sm" />
          <span className="text-xd-muted">%</span>
          <button type="button" onClick={saveDiscount} disabled={saving} data-testid="supplier-discount-save" className="h-10 px-4 rounded-lg bg-xd-orange text-white text-sm font-semibold hover:opacity-90 disabled:opacity-60">
            {saving ? "Salvataggio…" : "Salva sconto"}
          </button>
        </div>
      </div>

      {isSuper && (
        <div className="bg-white border border-blue-200 rounded-xl p-5 mb-6 max-w-2xl" data-testid="meta-pixel-card">
          <div className="flex items-center gap-2 font-admin font-semibold mb-3"><Facebook className="w-4 h-4 text-blue-600" /> Meta Pixel (Facebook / Instagram)</div>
          <p className="text-xs text-xd-muted mb-3">Inserisci l&apos;ID Pixel dal tuo Business Manager. Traccia automaticamente PageView su tutte le rotte del sito pubblico. Sezione riservata al super admin.</p>
          <div className="flex items-center gap-3 mb-3">
            <input type="text" placeholder="Es. 123456789012345" value={pixelId} onChange={(e) => setPixelId(e.target.value)} data-testid="meta-pixel-input" className="flex-1 h-10 px-3 rounded-lg border border-xd-border text-sm font-mono-tech" maxLength={64} />
          </div>
          <div className="flex items-center justify-between">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={pixelEnabled} onChange={(e) => setPixelEnabled(e.target.checked)} data-testid="meta-pixel-enabled" className="w-4 h-4 accent-blue-600" />
              Tracking attivo
            </label>
            <button type="button" onClick={savePixel} disabled={savingPixel} data-testid="meta-pixel-save" className="h-10 px-4 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-60">
              {savingPixel ? "Salvataggio…" : "Salva Pixel"}
            </button>
          </div>
          {!pixelId && <p className="text-[11px] text-xd-muted mt-2">Nessun pixel configurato. Il tracking non viene caricato.</p>}
        </div>
      )}

      {isSuper && (
        <div className="bg-white border border-orange-200 rounded-xl p-5 mb-6 max-w-2xl" data-testid="ga4-card">
          <div className="flex items-center gap-2 font-admin font-semibold mb-3"><BarChart3 className="w-4 h-4 text-orange-600" /> Google Analytics 4</div>
          <p className="text-xs text-xd-muted mb-3">Measurement ID (G-XXXXXXXXXX) dal tuo account GA4. Traccia automaticamente PageView + funnel (view_item, add_to_cart, begin_checkout, purchase) sul sito pubblico. Sezione riservata al super admin.</p>
          <div className="space-y-3">
            <div>
              <label className="text-[11px] text-xd-muted font-mono-tech uppercase tracking-widest">Measurement ID</label>
              <input type="text" placeholder="G-XXXXXXXXXX" value={ga4Id} onChange={(e) => setGa4Id(e.target.value)} data-testid="ga4-id-input" className="w-full h-10 px-3 rounded-lg border border-xd-border text-sm font-mono-tech mt-1" maxLength={32} />
            </div>
            <div>
              <label className="text-[11px] text-xd-muted font-mono-tech uppercase tracking-widest">API Secret (Measurement Protocol) <span className="text-[10px]">{ga4SecretConfigured ? "· ✓ già configurato" : "· opzionale"}</span></label>
              <input type="password" placeholder={ga4SecretConfigured ? "(lasciato invariato)" : "Lascia vuoto se non lo hai"} value={ga4Secret} onChange={(e) => setGa4Secret(e.target.value)} data-testid="ga4-secret-input" className="w-full h-10 px-3 rounded-lg border border-xd-border text-sm font-mono-tech mt-1" maxLength={64} autoComplete="off" />
              <p className="text-[10px] text-xd-muted mt-1">Serve solo per il Purchase server-side. Se lo lasci vuoto, il Purchase non arriverà a GA4 (gli altri eventi sì).</p>
            </div>
          </div>
          <div className="flex items-center justify-between mt-4">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={ga4Enabled} onChange={(e) => setGa4Enabled(e.target.checked)} data-testid="ga4-enabled" className="w-4 h-4 accent-orange-600" />
              Tracking attivo
            </label>
            <button type="button" onClick={saveGa4} disabled={savingGa4} data-testid="ga4-save" className="h-10 px-4 rounded-lg bg-orange-600 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-60">
              {savingGa4 ? "Salvataggio…" : "Salva GA4"}
            </button>
          </div>
          {!ga4Id && <p className="text-[11px] text-xd-muted mt-2">Nessun ID configurato. Il tracking GA4 non viene caricato.</p>}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6 max-w-4xl">
        <div className="bg-white border border-xd-border rounded-xl p-5">
          <div className="flex items-center gap-2 font-admin font-semibold mb-3"><Globe className="w-4 h-4 text-xd-cyanHover" /> Lingue attive</div>
          <div className="space-y-2">
            {LANGS.map((l) => (
              <label key={l.code} className="flex items-center justify-between p-2 border border-xd-border rounded-md text-sm">
                <span>{l.label} <span className="text-xd-muted text-xs font-mono">({l.code})</span></span>
                <input type="checkbox" defaultChecked={l.active} className="w-4 h-4 accent-xd-cyan" />
              </label>
            ))}
          </div>
        </div>

        <div className="bg-white border border-xd-border rounded-xl p-5">
          <div className="flex items-center gap-2 font-admin font-semibold mb-3"><DollarSign className="w-4 h-4 text-xd-orangeHover" /> Valute attive</div>
          <div className="space-y-2">
            {CURRENCIES.map((c) => (
              <label key={c.code} className="flex items-center justify-between p-2 border border-xd-border rounded-md text-sm">
                <span>{c.label}</span>
                <input type="checkbox" defaultChecked={c.active} className="w-4 h-4 accent-xd-cyan" />
              </label>
            ))}
          </div>
        </div>

        <div className="bg-white border border-xd-border rounded-xl p-5 md:col-span-2">
          <div className="flex items-center gap-2 font-admin font-semibold mb-3"><Users className="w-4 h-4 text-xd-success" /> Ruoli utente</div>
          <div className="space-y-2">
            {ROLES.map((r) => (
              <div key={r.role} className="p-3 border border-xd-border rounded-md flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold capitalize">{r.role}</div>
                  <div className="text-xs text-xd-muted">{r.desc}</div>
                </div>
                <span className="px-2 py-0.5 text-xs rounded bg-xd-cyan/10 text-xd-cyanHover">Predefinito</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
